import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Documents() {
  useEffect(() => {
    document.title = 'Documents & Resources | Thorn Ford Dental Laboratory';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Download essential forms and resources for dental professionals. Access prescription forms, turnaround times, warranty and return policy from Thorn Ford Dental Laboratory.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'dental laboratory forms, prescription form, turnaround times, warranty return policy, dental resources, Thorn Ford');
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/documents`);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Documents & Resources | Thorn Ford Dental Laboratory",
      "description": "Download essential forms and resources for dental professionals. Access prescription forms, turnaround times, warranty and return policy from Thorn Ford Dental Laboratory.",
      "url": `${siteUrl}/documents`,
      "publisher": {
        "@type": "Organization",
        "name": "Thorn Ford Dental Laboratory",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Documents",
            "item": `${siteUrl}/documents`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const documents = [
    {
      title: 'Prescription Form',
      description: 'Standard prescription form for all dental laboratory services',
      icon: 'ri-file-list-3-line',
      downloadUrl: 'https://drive.google.com/file/d/1iGpnqgRPQkw-86TFgF0OHkjHGrMjrD2J/view?usp=drivesdk'
    },
    {
      title: 'Turnaround Times',
      description: 'Basic turnaround times based on time in-lab',
      icon: 'ri-time-line',
      downloadUrl: '#'
    },
    {
      title: 'Warranty & Return Policy',
      description: 'Our warranty terms and return policy for laboratory work',
      icon: 'ri-shield-check-line',
      downloadUrl: '#'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="bg-blue-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Documents & Resources</h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Access all the forms, guides, and resources you need to work seamlessly with Thorn Ford Dental Laboratory
            </p>
          </div>
        </section>

        {/* Documents Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((doc, index) => (
                <a
                  key={index}
                  href={doc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-200 hover:border-blue-500 group cursor-pointer"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg group-hover:bg-blue-600 transition-colors duration-300">
                      <i className={`${doc.icon} text-2xl text-blue-600 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {doc.description}
                      </p>
                      <div className="flex items-center text-blue-600 text-sm font-medium">
                        <span className="whitespace-nowrap">Download PDF</span>
                        <i className="ri-download-2-line ml-2"></i>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-lg shadow-md p-8">
              <i className="ri-question-line text-5xl text-blue-600 mb-4"></i>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Additional Documents?</h2>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Our team is here to help you with any documentation or resources you need.
              </p>
              <a
                href="tel:+1234567890"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
